import React, { useState } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import { Box, FormControlLabel, MenuItem, Radio } from '@mui/material';
import { Select } from '@rentspree/component-2023.components.atoms.select';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

import { MultiStepLayout } from '../../components/templates/MultiStepLayout';
import {
  PaymentResponsibility,
  ApplicationType,
  PayeeConfiguration,
  ScreeningPackage,
  SCREENING_PACKAGES,
} from '../../types/tenantScreening';

export interface PayeeStepProps {
  payeeConfig: Partial<PayeeConfiguration>;
  selectedPackage?: ScreeningPackage | undefined;
  onPayeeConfigChange: (config: Partial<PayeeConfiguration>) => void;
  onNext: () => void;
  onBack: () => void;
  onHomeClick?: () => void;
  onExitClick?: () => void;
  isLoading?: boolean | undefined;
}

// Mock application types - will be fetched from API
const APPLICATION_TYPES = [
  {
    value: ApplicationType.RENTSPREE,
    label: 'RentSpree Application (Default)',
  },
  { value: ApplicationType.CUSTOM, label: 'Custom Application' },
];

export const PayeeStep: React.FC<PayeeStepProps> = ({
  payeeConfig,
  selectedPackage,
  onPayeeConfigChange,
  onNext,
  onBack,
  onHomeClick,
  onExitClick,
  isLoading = false,
}) => {
  const [localPayeeConfig, setLocalPayeeConfig] = useState<
    Partial<PayeeConfiguration>
  >({
    paymentResponsibility: PaymentResponsibility.APPLICANT,
    applicationType: ApplicationType.RENTSPREE,
    ...payeeConfig,
  });

  const selectedPackageInfo = SCREENING_PACKAGES.find(
    pkg => pkg.type === selectedPackage
  );
  const packagePrice = selectedPackageInfo?.price || 0;
  const isLandlordPaying =
    localPayeeConfig.paymentResponsibility === PaymentResponsibility.LANDLORD;

  const handlePaymentToggle = (responsibility: PaymentResponsibility) => {
    const updatedConfig = {
      ...localPayeeConfig,
      paymentResponsibility: responsibility,
    };
    setLocalPayeeConfig(updatedConfig);
    onPayeeConfigChange(updatedConfig);
  };

  const handleApplicationTypeChange = (value: string) => {
    const updatedConfig = {
      ...localPayeeConfig,
      applicationType: value as ApplicationType,
    };
    setLocalPayeeConfig(updatedConfig);
    onPayeeConfigChange(updatedConfig);
  };

  const handleNext = () => {
    if (!isLoading) {
      onNext();
    }
  };

  const getCostDisplay = () => {
    if (isLandlordPaying) {
      return `$${packagePrice.toFixed(2)}`;
    }
    return '$0.00';
  };

  return (
    <MultiStepLayout
      currentStep={6}
      total={7}
      currentSubStepPercentage={100}
      onBack={onBack}
      onNext={handleNext}
      onHomeClick={onHomeClick}
      onExitClick={onExitClick}
      backDisabled={isLoading}
      nextDisabled={isLoading}
      title='Just two quick details left'
      description='Who pays for the application fee and select your rental application.'
    >
      <Box
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          padding: { xs: 2, md: 4 },
        }}
      >
        {/* Application Fee Payment Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 24 }} />
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Who pays for the application fee?
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0 }}>
            <FormControlLabel
              value='applicant'
              control={<Radio sx={{ display: 'none' }} />}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '12px 24px',
                    backgroundColor: !isLandlordPaying ? '#222222' : '#f5f5f5',
                    color: !isLandlordPaying ? 'white' : '#333',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderRight: 'none',
                    '&:hover': {
                      backgroundColor: !isLandlordPaying
                        ? '#222222'
                        : '#e5e5e5',
                    },
                  }}
                >
                  <Typography variant='body1' sx={{ fontWeight: 500 }}>
                    Applicant Pays
                  </Typography>
                </Box>
              }
              sx={{ margin: 0, flex: 1 }}
              onClick={() =>
                handlePaymentToggle(PaymentResponsibility.APPLICANT)
              }
            />

            <FormControlLabel
              value='landlord'
              control={<Radio sx={{ display: 'none' }} />}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '12px 24px',
                    backgroundColor: isLandlordPaying ? '#222222' : '#f5f5f5',
                    color: isLandlordPaying ? 'white' : '#333',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: 'none',
                    '&:hover': {
                      backgroundColor: isLandlordPaying ? '#222222' : '#e5e5e5',
                    },
                  }}
                >
                  <Typography variant='body1' sx={{ fontWeight: 500 }}>
                    I'll Pay
                  </Typography>
                </Box>
              }
              sx={{ margin: 0, flex: 1 }}
              onClick={() =>
                handlePaymentToggle(PaymentResponsibility.LANDLORD)
              }
            />
          </Box>

          <Typography
            variant='body2'
            sx={{
              mt: 2,
              color: '#666',
              fontWeight: 400,
            }}
          >
            Your cost:{' '}
            <Box component='span' sx={{ color: '#333', fontWeight: 600 }}>
              {getCostDisplay()}
            </Box>
          </Typography>
        </Box>

        {/* Rental Application Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DescriptionIcon sx={{ color: '#1976d2', fontSize: 24 }} />
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Select rental application
            </Typography>
          </Box>

          <Select
            value={
              localPayeeConfig.applicationType || ApplicationType.RENTSPREE
            }
            onChange={e =>
              handleApplicationTypeChange(e.target.value as string)
            }
            fullWidth
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#94a3b8',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
              },
            }}
          >
            {APPLICATION_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </MultiStepLayout>
  );
};

export default PayeeStep;
